import { CdkStep } from '@angular/cdk/stepper';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoModule } from '@jsverse/transloco';
import { tap } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { SwitchLanguageService } from '../../../../core/services';
import { Language } from '../../../../domain/models';
import { AccountInfo } from '../../models';
import { AuthService, LocalAccountCreationService } from '../../services';
import { AccountSetupComponent } from '../account-setup/account-setup.component';
import { AccountStepperComponent } from '../account-stepper';
import { AccountVerificationComponent } from '../account-verification/account-verification.component';
import { AgeComponent } from '../age/age.component';
import { PreferredLanguageComponent } from '../preferred-language/preferred-language.component';

@Component({
    selector: 'app-account-creation',
    templateUrl: './account-creation.component.html',
    styleUrls: ['./account-creation.component.scss'],
    standalone: true,
    imports: [
        TranslocoModule,
        AccountStepperComponent,
        CdkStep,
        AccountSetupComponent,
        AccountVerificationComponent,
        PreferredLanguageComponent,
        AgeComponent,
        FlexLayoutModule,
        MatButtonModule
    ]
})
export class AccountCreationComponent implements OnInit {
    @ViewChild(AccountStepperComponent) stepper:
        | AccountStepperComponent
        | undefined;

    constructor(
        private readonly _auth: AuthService,
        private readonly _localAccount: LocalAccountCreationService,
        private readonly _switchLanguage: SwitchLanguageService,
        private _googleAnalyticsService: GoogleAnalyticsService
    ) {}

    ngOnInit(): void {
        // Start from a clean slate. The stepper always opens on the first question, so any
        // values still held from an earlier attempt are stale and must not be able to combine
        // with what is entered this time.
        this._localAccount.clear();
        this._googleAnalyticsService.pageView('Sign up: Preferred language');
    }

    languageChosen(language: Language): void {
        this._localAccount.preferredLanguageId = language.languageId;
        this._switchLanguage.switch(
            language.languageCode.trimRight().split('-')[0],
            false
        );
        this._googleAnalyticsService.pageView('Sign up: How old are you');
        this.stepper?.next();
    }

    ageRangeChosen(ageRange: number): void {
        this._localAccount.ageRange = ageRange;
        this._googleAnalyticsService.pageView('Sign up: Setup your account');
        this.stepper?.next();
    }

    accountInfoSet(info: AccountInfo): void {
        this._localAccount.firstName = info.firstName;
        this._localAccount.lastName = info.lastName;
        this._localAccount.emailOrPhoneNumber = info.emailOrPhoneNumber;
        this._localAccount.receivesNotifications = info.receivesNotifications;
        this._localAccount.acceptedPrivacyPolicy = info.acceptedPrivacyPolicy;
        this._googleAnalyticsService.pageView('Sign up: Verify your account');
        this._auth
            .requestCreateAccountCode(info.emailOrPhoneNumber)
            .pipe(tap(() => this.stepper?.next()))
            .subscribe();
    }

    codeProvided(code: number) {
        this._localAccount.securityCode = code;
        if (this._localAccount.complete) {
            this._auth
                .signUp(this._localAccount.createUserModel())
                .pipe(tap(() => this._localAccount.clear()))
                .subscribe();
        }
    }

    goBack(): void {
        this.stepper?.previous();
    }
}
